import { createCustomer } from "@app/lib/authentication";
import { Error } from "@app/types/misc";
import { authenticationErrorMessage } from "@app/utils/authentication";

let defaultPasswordHelperText = "";

document.addEventListener("DOMContentLoaded", () => {
  initForm();
  initRegisterPageRedirect();

  // Open the login drawer if `?login` is in the URL.
  const params = new URLSearchParams(window.location.search);
  if (params.has("register")) {
    window.history.pushState({}, document.title, "/");
    document.dispatchEvent(new CustomEvent("drawer:register:open"));
  }

  // Login button
  const $loginButton: HTMLButtonElement | null = document.querySelector(".button-login");
  if ($loginButton) {
    $loginButton.addEventListener("click", () => {
      document.dispatchEvent(new CustomEvent("drawer:register:close"));
      document.dispatchEvent(new CustomEvent("drawer:login:open"));
    });
  }
});

const initForm = () => {
  const $form: HTMLFormElement | null = document.querySelector(".form-register");
  const $submitButton: HTMLButtonElement | null = $form?.querySelector("button[type='submit']") || null;

  if (!$form || !$submitButton) return;

  const $firstNameInputWrapper: HTMLDivElement | null =
    $form?.querySelector("input[name='firstName']")?.closest(".snippet-input") || null;
  const $lastNameInputWrapper: HTMLDivElement | null =
    $form?.querySelector("input[name='lastName']")?.closest(".snippet-input") || null;
  const $emailInputWrapper: HTMLDivElement | null =
    $form?.querySelector("input[name='email']")?.closest(".snippet-input") || null;
  const $passwordInputWrapper: HTMLDivElement | null =
    $form?.querySelector("input[name='password']")?.closest(".snippet-input") || null;
  const $passwordInputHelper: HTMLDivElement | null = $passwordInputWrapper?.querySelector(".helper-text") || null;
  const $phoneInputWrapper: HTMLDivElement | null =
    $form?.querySelector("input[name='phone']")?.closest(".snippet-input") || null;

  if (
    !$firstNameInputWrapper ||
    !$lastNameInputWrapper ||
    !$emailInputWrapper ||
    !$passwordInputWrapper ||
    !$passwordInputHelper ||
    !$phoneInputWrapper
  ) {
    return;
  }

  defaultPasswordHelperText = $passwordInputHelper.innerText || "";

  $form.addEventListener("submit", async (e: any) => {
    e.preventDefault();

    cleanInputError($firstNameInputWrapper);
    cleanInputError($lastNameInputWrapper);
    cleanInputError($emailInputWrapper);
    cleanInputError($passwordInputWrapper);
    cleanInputError($phoneInputWrapper);

    $passwordInputHelper.innerText = defaultPasswordHelperText;
    $passwordInputHelper.classList.remove("hidden")

    $submitButton.disabled = true;

    const formData = new FormData(e.target) as any;
    const formProps = Object.fromEntries(formData);

    // Extra validation for password
    if (
      formProps.password.length < 8 ||
      /[A-Z]/.test(formProps.password) === false ||
      /[^A-Z]/i.test(formProps.password) === false
    ) {
      inputError($passwordInputWrapper);
      $submitButton.disabled = false;
      return;
    }

    const data = {
      firstName: formProps.firstName,
      lastName: formProps.lastName,
      email: formProps.email,
      password: formProps.password,
      acceptsMarketing: formProps.acceptsMarketing === "on",
      ...(formProps.phone && { phone: formProps.phone }),
    };

    const res = await createCustomer(data);

    if (res.customerUserErrors.length > 0) {
      const error = {
        ...res.customerUserErrors[0],
        field: res.customerUserErrors[0].field[res.customerUserErrors[0].field.length - 1],
      };

      switch (error.field) {
        case "lastName":
          inputError($lastNameInputWrapper, error);
          break;
        case "email":
          inputError($emailInputWrapper, error);
          break;
        case "password":
          inputError($passwordInputWrapper);
          break;
        case "phone":
          inputError($phoneInputWrapper, error);
          break;
        default:
          inputError($firstNameInputWrapper, error);
          break;
      }
    } else {
      document.dispatchEvent(new CustomEvent("drawer:register:close"));
      document.dispatchEvent(
        new CustomEvent("drawer:login:open", {
          detail: { email: formProps.email, password: formProps.password, submit: true },
        }),
      );
    }

    $submitButton.disabled = false;
  });

  return;
};

const inputError = ($inputWrapper: HTMLDivElement, error?: Error) => {
  const $helperText: HTMLDivElement | null = $inputWrapper.querySelector(".helper-text");
  if (!$helperText) return;
  $inputWrapper.classList.add("snippet-input--error");

  let errorMessage = error?.message;

  if (error?.code && authenticationErrorMessage[error.code]) {
    errorMessage = authenticationErrorMessage[error.code];
  }

  if (errorMessage) {
    $helperText.innerText = errorMessage;
  }

  $helperText.classList.remove("hidden");
};

const cleanInputError = ($inputWrapper: HTMLDivElement) => {
  const $helperText: HTMLDivElement | null = $inputWrapper.querySelector(".helper-text");
  if (!$helperText) return;
  $inputWrapper.classList.remove("snippet-input--error");
  $helperText.classList.add("hidden");
};

/**
 * Will redirect to the homepage if a user lands on the login page.
 */
const initRegisterPageRedirect = () => {
  if (window.location.pathname === "/account/register") {
    window.location.replace(window.customer ? "/account" : "/?register");
  }
};
